'use client'
import { useEffect, useId, useMemo, useState, memo } from 'react'
import type { JSX } from 'react'
import styled from 'styled-components'
import { useModal } from '@/hooks'
import { iconByName } from '@/utils'
import { mq, vw, getP14, rgbAlpha } from '@/styles'
import { Figure } from '../../Figure'
import { useDropdown } from '../../dropdown/useDropdown'
import { Div, Label, Ul, arrowIcon, optionsListVariants } from './styles'
import type { TypeSelectBase, TypeSelectOption } from './types'
import { useSelectSearch } from './useSelectSearch'

type TypeSelectValue = string | number
type TypeSelectMultiple = Omit<TypeSelectBase, 'register'> & { defaultValue?: TypeSelectValue[] }

function isSameSelection(a: TypeSelectValue[], b: TypeSelectValue[]) {
  return a.length === b.length && a.every((value, idx) => value === b[idx])
}

// Keeps the incoming order so a selection is never silently reshuffled into the options order
function resolveSelection(incoming: TypeSelectValue[], options: TypeSelectOption[]) {
  return incoming.filter(value => options.some(option => option.value === value))
}

const getOptionsList = (data: TypeSelectOption[], onClickOption: (option: TypeSelectOption) => void, selectedValues: TypeSelectValue[]) => data.map((option, idx) => {
  const isSelected = selectedValues.includes(option.value)

  return (
    <li key={`${option.value}-${idx}`} role='presentation' className={`${isSelected ? 'current' : ''}${option.store ? ` online-store${data[idx + 1]?.store ? '' : ' last-store'}` : ''}`}>
      {/* mousedown only keeps the focus on the searcher so several options can be picked in a row. The selection
      runs on click so this node is still mounted when useDropdown's document mousedown checks for an outside click */}
      <button type='button' role='option' aria-selected={isSelected} onMouseDown={e => e.preventDefault()} onClick={() => onClickOption(option)}>
        {option.name}
        {option.store && <span className='online-store'>Tienda online</span>}
      </button>
    </li>
  )
})

export const SelectMultiple = memo(({ options, name, defaultValue, label, autoComplete = 'off', placeholder, placeholderVisible = false, required = false, disabled = false, readOnly = null, onChange = null, col, icon = null, className, textColor = 'text', bgColor = 'background', error, setFormValue, dropdownTextColor = null, modalId, payloadKey }: TypeSelectMultiple): JSX.Element => {
  const { isActive, payload } = useModal(modalId)
  const listId = useId()

  const [selectedValues, setSelectedValues] = useState<TypeSelectValue[]>(defaultValue ?? [])
  const [searchValue, setSearchValue] = useState('')

  const { currentOptions, filterOptions, resetOptions } = useSelectSearch({ options })
  const [triggerRef, dropdownRef, expanded, onTriggerFocus] = useDropdown()

  const selectedOptions = useMemo(() => selectedValues.map(value => options.find(option => option.value === value)).filter(Boolean), [selectedValues, options])
  const hasSelection = selectedOptions.length > 0
  const isListRendered = !readOnly

  const onClickOption = (option: TypeSelectOption) => {
    const isSelected = selectedValues.includes(option.value)
    setSelectedValues(isSelected ? selectedValues.filter(value => value !== option.value) : [...selectedValues, option.value])
    setSearchValue('')
    resetOptions()
  }

  const onClickRemove = (value: TypeSelectValue) => setSelectedValues(selectedValues.filter(item => item !== value))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Options = useMemo(() => getOptionsList(currentOptions, onClickOption, selectedValues), [currentOptions, selectedValues])

  const handleSearch = e => {
    const value = e?.target?.value ?? ''
    setSearchValue(value)
    onChange?.(e)
    filterOptions(value)
  }

  const onBlurSearch = () => {
    setSearchValue('')
    resetOptions()
  }

  // Single writer towards the form: the selection state is the source of truth
  useEffect(() => { setFormValue?.(name, selectedValues) }, [selectedValues, name, setFormValue])

  useEffect(() => {
    if (!defaultValue?.length) return
    const values = resolveSelection(defaultValue, options)
    if (!values.length) return
    setSelectedValues(current => isSameSelection(current, values) ? current : values)
  }, [defaultValue, options])

  useEffect(() => {
    // This useEffect will only run if the <SelectMultiple /> is inside a modal, prefilling the
    // selection from the payload every time the modal opens and clearing it when it closes.
    if (!modalId) return

    const payloadValue = isActive && payloadKey ? payload?.[payloadKey] : null
    const incomingValues = Array.isArray(payloadValue) ? payloadValue : payloadValue != null ? [payloadValue] : []
    const values = resolveSelection(incomingValues, options)

    setSelectedValues(current => isSameSelection(current, values) ? current : values)
    setSearchValue('')
  }, [isActive, payload, payloadKey, options, modalId])

  return (
    <DivMultiple ref={triggerRef} $col={col} $readOnly={readOnly} $hasSelection={hasSelection}>
      <Label $hasContent={!!searchValue || hasSelection} $placeholderVisible={placeholderVisible} $textColor={textColor} $bgColor={bgColor} {...className && { className }}>
        <input type='text' name={`${name}_searcher`} aria-invalid={!!error} required={required && !hasSelection} disabled={disabled} readOnly={readOnly} placeholder={placeholder ?? label} onChange={handleSearch} onFocus={onTriggerFocus} onClick={onTriggerFocus} onBlur={onBlurSearch} value={searchValue} autoComplete={autoComplete}
          {...isListRendered && { role: 'combobox', 'aria-expanded': expanded, 'aria-controls': listId, 'aria-autocomplete': 'list' as const }}
        />
        {label && <span>{label}</span>}
        {icon ? icon : <Figure media={arrowIcon} fill={false} lazy={true} className='chevron' />}
      </Label>
      {isListRendered && <Ul ref={dropdownRef} id={listId} role='listbox' aria-multiselectable={true} {...label && { 'aria-label': label }} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'} $textColor={textColor} $dropdownTextColor={dropdownTextColor}>
        {Options}
      </Ul>}
      {hasSelection && <UlChips aria-live='polite'>
        {selectedOptions.map(option => (
          <li key={option.value}>
            <span>{option.name}</span>
            {!readOnly && <button type='button' aria-label={`Quitar ${option.name}`} onClick={() => onClickRemove(option.value)}>
              <Figure media={iconByName('search-clear')} fill={false} lazy={true} />
            </button>}
          </li>
        ))}
      </UlChips>}
    </DivMultiple>
  )
})

SelectMultiple.displayName = 'SelectMultiple'

const DivMultiple = styled(Div)<{ $hasSelection: boolean }>`
  ${Label} {
    margin-bottom: ${({ $hasSelection }) => $hasSelection ? vw(10, 'mobile') : vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${({ $hasSelection }) => $hasSelection ? vw(10, 'tablet') : vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${({ $hasSelection }) => $hasSelection ? vw(10, 'desktop') : vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: ${({ $hasSelection }) => $hasSelection ? '10px' : '30px'};
    }
  }
`

const UlChips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${vw(6, 'mobile')};
  margin-bottom: ${vw(20, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    gap: ${vw(6, 'tablet')};
    margin-bottom: ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    gap: ${vw(6, 'desktop')};
    margin-bottom: ${vw(30, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    gap: 6px;
    margin-bottom: 30px;
  }

  li {
    align-items: center;
    background-color: ${rgbAlpha('--color-blue', .1)};
    border-radius: 20px;
    display: flex;
    gap: 6px;
    max-width: 100%;
    padding: 4px 6px 4px 12px;
  }

  span {
    ${getP14()}
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    height: 16px;
    justify-content: center;
    opacity: .5;
    transition: 300ms opacity ease-out;
    width: 16px;

    &:hover,
    &:focus-visible {
      opacity: 1;
    }

    figure,
    img,
    svg {
      height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }
`
